
import { useState } from 'react';
import { OnboardingStep } from './useOnboardingState';
import { supabase } from "@/integrations/supabase/client";
import { getStoredOtp, clearStoredOtp } from '@/utils/otp-utils';

interface OtpVerifierProps {
  email: string;
  name: string;
  setStep: (step: OnboardingStep) => void;
  loadUserData: () => void;
}

export const useOtpVerifier = ({
  email,
  name,
  setStep,
  loadUserData
}: OtpVerifierProps) => {
  const [isVerifying, setIsVerifying] = useState(false);
  
  // Verify the OTP and complete user verification
  const handleOtpComplete = async (otp: string): Promise<boolean> => {
    console.log("=== OTP VERIFICATION STARTED ===");
    console.log("User entered OTP:", otp);
    console.log("User entered OTP type:", typeof otp);
    console.log("User entered OTP length:", otp.length);
    
    // Log current onboarding step for debugging
    const currentStep = localStorage.getItem('currentOnboardingStep');
    console.log("Current onboarding step in localStorage:", currentStep);
    
    // Debug all localStorage OTP-related contents
    console.log("=== LOCALSTORAGE FULL DEBUG ===");
    const storedOtp = localStorage.getItem('currentOtp');
    const storedExpiry = localStorage.getItem('otpExpiry');
    console.log("currentOtp:", storedOtp);
    console.log("otpExpiry:", storedExpiry);
    console.log("All localStorage keys:", Object.keys(localStorage));
    
    // Basic validation
    if (!otp || otp.length !== 6) {
      console.log("OTP validation failed: invalid length or empty");
      alert("Veuillez entrer un code de 6 chiffres");
      return false;
    }
    
    setIsVerifying(true);
    
    try {
      // Get the stored OTP to compare
      console.log("Calling getStoredOtp()...");
      const { otp: storedOtp, isExpired } = getStoredOtp();
      
      console.log("=== OTP RETRIEVAL RESULTS ===");
      console.log("- Stored OTP:", storedOtp);
      console.log("- Stored OTP type:", typeof storedOtp);
      console.log("- Is expired:", isExpired);
      
      if (!storedOtp) {
        console.error("CRITICAL ERROR: No stored OTP found!");
        console.log("This means OTP was either never generated or was cleared prematurely");
        alert("Aucun code de vérification trouvé. Veuillez demander un nouveau code de vérification.");
        return false;
      }
      
      if (isExpired) {
        console.log("Stored OTP is expired");
        clearStoredOtp();
        alert("Le code a expiré. Veuillez demander un nouveau code");
        return false;
      }
      
      // String comparison with detailed logging
      console.log("=== COMPARING CODES ===");
      console.log("Entered code:", `'${otp}'`);
      console.log("Expected code:", `'${storedOtp}'`);
      console.log("Are they equal (===):", otp === storedOtp);
      console.log("Are they equal (==):", otp == storedOtp);
      
      // Detailed character comparison
      console.log("Character by character comparison:");
      const maxLength = Math.max(otp.length, storedOtp.length);
      for (let i = 0; i < maxLength; i++) {
        const enteredChar = otp[i] || 'undefined';
        const storedChar = storedOtp[i] || 'undefined';
        console.log(`Position ${i}: '${enteredChar}' vs '${storedChar}' - Match: ${enteredChar === storedChar}`);
      }
      
      if (otp !== storedOtp) {
        console.log("OTP codes don't match");
        alert("Le code saisi ne correspond pas. Veuillez vérifier et réessayer");
        return false;
      }
      
      console.log("OTP verified successfully, proceeding with user creation");
      
      // Clear the used OTP immediately
      clearStoredOtp();
      
      // Create or update user in database
      console.log("Creating/updating user data");
      
      // First, check if user already exists
      const { data: existingUsers, error: checkError } = await supabase
        .from('users')
        .select('id, email')
        .eq('email', email.toLowerCase().trim());
        
      if (checkError) {
        console.error("Error checking existing user:", checkError);
        throw new Error("Erreur lors de la vérification. Veuillez réessayer.");
      }
      
      let userData;
      
      if (existingUsers && existingUsers.length > 0) {
        // Update existing user
        console.log("Updating existing user:", existingUsers[0].id);
        const { data: updatedData, error: updateError } = await supabase
          .from('users')
          .update({ name: name.trim() })
          .eq('id', existingUsers[0].id)
          .select();
          
        if (updateError) {
          console.error("Update error:", updateError);
          throw new Error("Erreur lors de la mise à jour. Veuillez réessayer.");
        }
        
        userData = updatedData;
      } else {
        // Create new user
        console.log("Creating new user");
        const { data: newData, error: insertError } = await supabase
          .from('users')
          .insert({
            name: name.trim(),
            email: email.toLowerCase().trim(),
          })
          .select();
          
        if (insertError) {
          console.error("Insert error:", insertError);
          throw new Error("Erreur lors de la création. Veuillez réessayer.");
        }
        
        userData = newData;
      }

      console.log("User data saved successfully:", userData);

      // Save to localStorage for client-side access
      const localUserData = {
        name: name.trim(),
        email: email.toLowerCase().trim(),
        verified: true,
        onboardingCompleted: true,
        lastVerified: new Date().toISOString()
      };
      
      localStorage.setItem('userData', JSON.stringify(localUserData));
      console.log("User data saved to localStorage:", localUserData);
      
      // Reload user data from localStorage
      loadUserData();
      
      // Move to success step
      setStep('success');
      
      console.log("Vérification réussie - compte configuré avec succès!");
      
      return true;
    } catch (error) {
      console.error("=== OTP VERIFICATION ERROR ===");
      console.error("Error details:", error);
      
      const errorMessage = error instanceof Error ? error.message : "Une erreur s'est produite lors de la vérification";
      
      alert(errorMessage);
      return false;
    } finally {
      setIsVerifying(false);
      console.log("=== OTP VERIFICATION ENDED ===");
    }
  };
  
  return {
    handleOtpComplete,
    isVerifying
  };
};
